import { initializeDB } from '../config/db';
import { FALL_ARREST_RESCUE_PLAN_STORE } from '../config/constants';
import axiosInstance from '../axiosInstance';
import { loadTriggers, evaluateTriggers, SPECIAL_FARP } from '../components/jha/utils/documentTriggerEvaluator';

// Local statuses (extend backend draft/submitted with pending for offline queue):
//   'draft'             — local-only, not sent
//   'pending_submit'    — submit attempted but queued (offline / network error)
//   'submitted'         — backend accepted (только в БД на сервере, в IDB удалён)
const STATUS_PENDING_SUBMIT = 'pending_submit';

const fallArrestRescuePlanService = {
    /**
     * Submit form to backend with offline-queue fallback.
     *
     * Behaviour:
     *  - Online + server OK   → returns { success: true, id, uuid }, draft удаляется вызывающим кодом.
     *  - Online + server 4xx/5xx → returns { success: false, message } — draft остаётся.
     *  - Offline / network err → marks IDB record status='pending_submit',
     *                            returns { success: false, queued: true, uuid }.
     *
     * Throws ONLY на критических ошибках (не сетевых). Network failures
     * обрабатываются graceful'но через очередь.
     */
    async submit(plan) {
        const onlineGuess = typeof navigator !== 'undefined' ? navigator.onLine : true;

        // Try online submission only if browser thinks we're online
        if (onlineGuess) {
            try {
                const response = await axiosInstance.post('/fall-arrest-rescue-plan/submit', plan);
                if (response?.data?.success) {
                    // Backend accepted — фиксируем submitted-mirror в IDB
                    // (status='submitted' + submittedAt + serverId). Запись
                    // остаётся локально, чтобы checkExisting видел её offline.
                    try {
                        const db = await initializeDB();
                        const now = new Date().toISOString();
                        const mirror = {
                            ...plan,
                            id:           response.data.id ?? plan.id ?? null,
                            status:       'submitted',
                            submittedAt:  plan.submittedAt || now,
                            updatedAt:    now,
                        };
                        await db.put(FALL_ARREST_RESCUE_PLAN_STORE, mirror);
                    } catch (mirrorErr) {
                        console.warn('Failed to mirror submitted FARP to IDB:', mirrorErr);
                    }
                    return response.data; // { success, id, uuid, status, message }
                }
                // Server rejected (validation etc.) — return as-is, не очередим
                return response?.data || { success: false, message: 'Empty server response' };
            } catch (e) {
                // axios bucket: response present = server error; response absent = network/CORS
                if (e?.response) {
                    return {
                        success: false,
                        message: e.response.data?.message || e.message || 'Server error',
                    };
                }
                // network error — fall through to queue
                console.warn('FARP submit network error, queuing for sync:', e.message);
            }
        }

        // Offline / network error → save with status='pending_submit'
        try {
            const queued = await this.saveDraft({ ...plan, status: STATUS_PENDING_SUBMIT });
            return { success: false, queued: true, uuid: queued.uuid };
        } catch (e) {
            console.error('Failed to queue FARP submission:', e);
            return { success: false, message: 'Failed to queue offline: ' + (e.message || 'unknown') };
        }
    },

    /**
     * Sync all pending_submit records — retry each via POST.
     * Called: on app start (App.jsx), on `online` window event, on manual sync.
     *
     * Idempotent: if a record fails, it stays 'pending_submit' for next try.
     * Returns { synced: number, failed: number, total: number }.
     */
    async syncPending() {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return { synced: 0, failed: 0, total: 0, skippedOffline: true };
        }
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) {
            return { synced: 0, failed: 0, total: 0 };
        }

        // Snapshot pending records (don't hold tx across network calls)
        const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
        const pending = (all || []).filter(p => p.status === STATUS_PENDING_SUBMIT);

        let synced = 0, failed = 0;
        for (const record of pending) {
            try {
                const payload = { ...record, status: 'submitted' };
                const resp = await axiosInstance.post('/fall-arrest-rescue-plan/submit', payload);
                if (resp?.data?.success) {
                    // Server accepted — переводим в submitted (НЕ удаляем),
                    // чтобы checkExisting видел его в IDB как уже поданный.
                    const now = new Date().toISOString();
                    const mirror = {
                        ...record,
                        id:          resp.data.id ?? record.id ?? null,
                        status:      'submitted',
                        submittedAt: record.submittedAt || now,
                        updatedAt:   now,
                    };
                    await db.put(FALL_ARREST_RESCUE_PLAN_STORE, mirror);
                    synced++;
                } else {
                    // Server rejected — keep pending, log message
                    console.warn('FARP sync rejected by server:', resp?.data?.message);
                    failed++;
                }
            } catch (e) {
                // Network error — leave as pending, will retry next time
                console.warn('FARP sync error for', record.uuid, ':', e.message);
                failed++;
            }
        }

        if (synced > 0) {
            // Notify UI components to refresh list
            window.dispatchEvent(new CustomEvent('farpSynced', { detail: { synced, total: pending.length } }));
        }
        return { synced, failed, total: pending.length };
    },

    /**
     * Save (or update) draft locally in IndexedDB by uuid.
     * Status defaults to 'draft' if not set.
     */
    async saveDraft(plan) {
        const db = await initializeDB();
        const now = new Date().toISOString();

        const toSave = {
            ...plan,
            updatedAt: now,
        };

        if (!toSave.status) {
            toSave.status = 'draft';
        }
        if (!toSave.createdAt) {
            toSave.createdAt = now;
        }

        await db.put(FALL_ARREST_RESCUE_PLAN_STORE, toSave);
        return toSave;
    },

    /**
     * Load a single draft by uuid.
     */
    async loadByUuid(uuid) {
        const db = await initializeDB();
        return await db.get(FALL_ARREST_RESCUE_PLAN_STORE, uuid);
    },

    /**
     * Get all entries (drafts + submitted mirror) from IDB.
     */
    async getAll() {
        const db = await initializeDB();
        return await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
    },

    /**
     * Delete a record by uuid.
     */
    async delete(uuid) {
        const db = await initializeDB();
        await db.delete(FALL_ARREST_RESCUE_PLAN_STORE, uuid);
    },

    /**
     * Find an existing draft (status != 'submitted') for a specific JHA (localId).
     * Most precise lookup — used when the FARP form is opened from a JHA banner.
     *
     * Returns the draft object or null.
     */
    async findDraftByJhaLocalId(jhaLocalId) {
        if (!jhaLocalId) return null;
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) return null;
        const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
        const matches = (all || [])
            .filter(p => p.status !== 'submitted' && p.jhaLocalId === jhaLocalId)
            .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
        return matches[0] || null;
    },

    /**
     * Find an existing draft for (jobCode, planDate). Less precise than
     * findDraftByJhaLocalId — used as fallback for standalone FARP forms
     * (opened from menu, not from JHA).
     */
    async findDraftByJobAndDate(jobCode, planDate) {
        if (!jobCode || !planDate) return null;
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) return null;
        const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
        const matches = (all || [])
            .filter(p => p.status !== 'submitted'
                && p.jobCode === jobCode
                && p.planDate === planDate
                && !p.jhaLocalId)  // не привязанные к JHA — иначе перепутаем разные работы за день
            .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
        return matches[0] || null;
    },

    /**
     * Build a "copy" of the latest submitted FARP — for the "Copy from previous"
     * button. Strips identity fields (uuid/id/dates/signature/submitter) so the
     * caller can apply onto a fresh form preserving its own uuid/jhaLocalId/etc.
     *
     * Criteria priority:
     *   1) Same jobCode (preferred — most relevant)
     *   2) Any latest submitted (fallback)
     *
     * Returns a "patch" object to merge into the current form, or null if nothing
     * to copy from. Caller is responsible for keeping its own:
     *   uuid, status, planDate, submitter*, jhaLocalId, createdAt
     */
    async buildCopyFromPrevious(criteria = {}) {
        const db = await initializeDB();
        if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) return null;
        const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
        const submitted = (all || [])
            .filter(p => p.status === 'submitted')
            .sort((a, b) => new Date(b.submittedAt || b.updatedAt || 0) - new Date(a.submittedAt || a.updatedAt || 0));

        let src = null;
        if (criteria.jobCode) {
            src = submitted.find(p => p.jobCode === criteria.jobCode) || null;
        }
        if (!src) {
            src = submitted[0] || null;
        }
        if (!src) return null;

        // Возвращаем "patch" — только поля содержания, без идентификации
        return {
            source: {
                uuid:        src.uuid,
                jobCode:     src.jobCode,
                planDate:    src.planDate,
                submittedAt: src.submittedAt,
                submitterName: src.submitterName,
            },
            patch: {
                jobCode:             src.jobCode,
                jobName:             src.jobName,
                jobLocation:         src.jobLocation,
                jobDescription:      src.jobDescription,
                customerName:        src.customerName,
                rescuers:            src.rescuers,
                competentPerson:     src.competentPerson,
                emergencyContact:    src.emergencyContact,
                methodOfContact:     src.methodOfContact,
                rescueEquipment:     src.rescueEquipment,
                equipmentLocation:   src.equipmentLocation,
                anchorPoint:         src.anchorPoint,
                landingArea:         src.landingArea,
                obstructionsHazards: src.obstructionsHazards,
                checklist:           src.checklist,
                preWorkTasks:        src.preWorkTasks,
                responseProcedure:   src.responseProcedure,
            },
        };
    },

    /**
     * Link an existing local FARP record to a specific JHA (jhaLocalId).
     * Used from FARPList "Link to JHA" action.
     */
    async linkToJha(farpUuid, jhaLocalId) {
        if (!farpUuid || !jhaLocalId) return false;
        const db = await initializeDB();
        const rec = await db.get(FALL_ARREST_RESCUE_PLAN_STORE, farpUuid);
        if (!rec) return false;
        rec.jhaLocalId = jhaLocalId;
        rec.updatedAt = new Date().toISOString();
        await db.put(FALL_ARREST_RESCUE_PLAN_STORE, rec);
        return true;
    },

    /**
     * Find latest submitted plan for a given jobCode (used for "Copy from previous"
     * and JHA cross-link checks).
     */
    async findLatestSubmittedByJob(jobCode) {
        if (!jobCode) return null;
        const db = await initializeDB();
        const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
        const matches = (all || [])
            .filter(p => p.status === 'submitted' && p.jobCode === jobCode)
            .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
        return matches[0] || null;
    },

    /**
     * Default checklist seed (matches backend FallArrestRescuePlan::getDefaultChecklist).
     */
    getDefaultChecklist() {
        return [
            { id: 1, question: 'Have alternatives to using fall arrest equipment been considered?',                        answer: null, comment: '' },
            { id: 2, question: 'Has rescue equipment been inspected and found in good shape?',                              answer: null, comment: '' },
            { id: 3, question: 'Is equipment adequate for the rescue plan (weight ratings, length, connection type, etc.)?', answer: null, comment: '' },
            { id: 4, question: 'Have communication devices been identified, located and tested?',                           answer: null, comment: '' },
            { id: 5, question: 'Are all rescuers familiar with the use of the rescue equipment?',                           answer: null, comment: '' },
            { id: 6, question: 'Can Rescue Method be performed within 15 minutes?',                                         answer: null, comment: '' },
            { id: 7, question: 'No Rescue method is able to be performed — task delayed.',                                  answer: null, comment: '' },
        ];
    },

    /**
     * Default Response Procedure seed (first 3 lines per the updated form).
     */
    getDefaultResponseProcedure() {
        return [
            { order: 1, text: 'Notify Emergency Contact',          isDefault: true },
            { order: 2, text: 'Make medical assessment of person', isDefault: true },
            { order: 3, text: 'Place them on a board',             isDefault: true },
        ];
    },

    /**
     * Default Method of Contact shape.
     * - faceToFace             — простой toggle
     * - radio.channels         — список каналов (несколько)
     * - phone.numbers          — список номеров (несколько)
     * - custom                 — список {label, value} для произвольных методов
     *                            (юзер сам пишет название метода)
     */
    getDefaultMethodOfContact() {
        return {
            faceToFace: false,
            radio: { enabled: false, channels: [] },
            phone: { enabled: false, numbers: [] },
            custom: [],
        };
    },

    /**
     * Default Rescue Equipment.
     * Активные ключи: mewps, ladder, rescuePositioningDevice, manualEntry.
     * customs[] — массив строк для "Other equipment - prompt for description".
     */
    getDefaultRescueEquipment() {
        return {
            mewps: false,
            ladder: false,
            rescuePositioningDevice: false,
            manualEntry: false,
            customs: [],
        };
    },

    /**
     * Default Equipment Location.
     * 3 предопределённых расстояния + customs (Other, несколько).
     * Labels — только расстояния, без названий мест (Safety Office / Tool House / Gang Box убраны).
     */
    getDefaultEquipmentLocation() {
        return {
            within100: false,
            from100to300: false,
            over300: false,
            customs: [],
        };
    },

    // ───────────────────────── JHA-integration helpers ─────────────────────────

    /**
     * Backward-compat: список WorkActivities, которые триггерят FARP
     * через простой Work-Activity-only condition. Используется баннером в
     * Step 3 / Step 4. Современная логика триггеров поддерживает также
     * Energy + Hazard — это охватывается evaluateForStep на Step 2.
     *
     * Возвращает string[] из workActivityValue одиночных conditions FARP-триггера.
     */
    async getTriggerValues() {
        try {
            const triggers = await loadTriggers();
            const farp = triggers.find(t => t.specialDocumentType === SPECIAL_FARP);
            if (!farp || !Array.isArray(farp.conditions)) return [];
            return farp.conditions
                .filter(c => c.workActivityValue && !c.energySourceValue && !c.hazardValue)
                .map(c => c.workActivityValue);
        } catch (e) {
            console.error('Error reading FARP trigger values:', e);
            return [];
        }
    },

    /**
     * Check whether a given workActivity value triggers FARP via a simple
     * Work-Activity-only condition. Multi-field conditions are evaluated
     * separately by documentTriggerEvaluator on Step 2.
     */
    async isTrigger(workActivityValue) {
        if (!workActivityValue) return false;
        const values = await this.getTriggerValues();
        return values.includes(workActivityValue);
    },

    /**
     * Check whether a submitted FARP exists IN LOCAL IndexedDB.
     * Никаких GET к серверу — фронт читает только IDB. Submitted записи
     * остаются в IDB после успешного submit (с status='submitted'), поэтому
     * этот lookup всегда видит то что юзер сам подал на этом устройстве.
     *
     * Multi-user trade-off: запись поданная другим юзером с другого iPad
     * не будет видна — это OK, потому что один JHA принадлежит одной бригаде
     * и FARP подаёт её competent person.
     *
     * Priority: { jhaLocalId } > { jobCode, date }.
     */
    async checkExisting(criteria) {
        // Backwards-compat: legacy callers pass (jobCode, date) as 2 args.
        if (typeof criteria === 'string') {
            const [jobCode, date] = arguments;
            criteria = { jobCode, date };
        }
        const { jhaLocalId, jobCode, date } = criteria || {};
        if (!jhaLocalId && (!jobCode || !date)) {
            return { exists: false, plan: null };
        }

        try {
            const db = await initializeDB();
            if (!db.objectStoreNames.contains(FALL_ARREST_RESCUE_PLAN_STORE)) {
                return { exists: false, plan: null };
            }
            const all = await db.getAll(FALL_ARREST_RESCUE_PLAN_STORE);
            const submitted = (all || []).filter(p => p.status === 'submitted');

            // Точный match по jhaLocalId
            let match = null;
            if (jhaLocalId) {
                match = submitted.find(p => p.jhaLocalId === jhaLocalId);
            }
            // Fallback по job+date (только записи без jhaLocalId — иначе перепутаем
            // разные работы за один день)
            if (!match && jobCode && date) {
                match = submitted.find(p =>
                    !p.jhaLocalId && p.jobCode === jobCode && p.planDate === date
                );
            }

            if (!match) return { exists: false, plan: null };

            return {
                exists: true,
                plan: {
                    id:            match.id ?? null,
                    uuid:          match.uuid,
                    status:        match.status,
                    planDate:      match.planDate,
                    submittedAt:   match.submittedAt,
                    submitterName: match.submitterName ?? match.submittedByName,
                    jhaLocalId:    match.jhaLocalId,
                },
            };
        } catch (e) {
            console.warn('checkExisting (IDB) failed:', e.message);
            return { exists: false, plan: null };
        }
    },
};

export default fallArrestRescuePlanService;
